/**
 * Integrity check service for validating shift operations
 * Performs dry-run validation for copy operations and series creation
 */

/**
 * Main integrity check service
 */
export const integrityCheckService = {
    /**
     * Validates shifts before copying
     * @param {Array} newShifts - Array of shift objects to validate
     * @param {Object} event - Event object with start/end dates
     * @param {Array} existingShifts - Current shifts in the event
     * @returns {Object} Validation result with issues and recommendations
     */
    validateShiftCopy: async (newShifts, event, existingShifts) => {
        const issues = [];

        // Check date boundaries
        const boundaryIssues = checkDateBoundaries(newShifts, event);
        issues.push(...boundaryIssues);

        // Check for duplicates
        const duplicateIssues = checkForDuplicates(newShifts, existingShifts);
        issues.push(...duplicateIssues);

        return {
            hasIssues: issues.length > 0,
            issues,
            canProceed: true // Allow proceeding even with errors - user can decide
        };
    },

    /**
     * Validates series creation
     * @param {Array} seriesShifts - Array of shifts that would be created
     * @param {Object} event - Event object
     * @param {Array} existingShifts - Current shifts in the event
     * @returns {Object} Validation result
     */
    validateSeriesCreation: async (seriesShifts, event, existingShifts) => {
        const issues = [];

        // Check date boundaries
        const boundaryIssues = checkDateBoundaries(seriesShifts, event);
        issues.push(...boundaryIssues);

        // Check for duplicates within series and against existing
        const duplicateIssues = checkForDuplicates(seriesShifts, existingShifts);
        issues.push(...duplicateIssues);

        // Check for internal series duplicates (shouldn't happen but safety check)
        const internalDuplicates = checkSeriesInternalDuplicates(seriesShifts);
        issues.push(...internalDuplicates);

        return {
            hasIssues: issues.length > 0,
            issues,
            canProceed: true // Allow proceeding even with errors - user can decide
        };
    }
};

/**
 * Checks for duplicate shifts based on name, date, times, and slots
 */
const checkForDuplicates = (newShifts, existingShifts) => {
    const issues = [];

    for (const newShift of newShifts) {
        const duplicates = existingShifts.filter(existing =>
            isDuplicateShift(newShift, existing)
        );

        if (duplicates.length > 0) {
            issues.push({
                type: 'duplicate',
                severity: 'error', // Prevents creation
                shift: newShift,
                conflicts: duplicates,
                message: `Duplicate shift "${newShift.name}" conflicts with ${duplicates.length} existing shift(s) on ${newShift.date}`
            });
        }
    }

    return issues;
};

/**
 * Determines if two shifts are duplicates
 */
const isDuplicateShift = (shift1, shift2) => {
    // Must have same name, date, start time, end time
    if (shift1.name !== shift2.name) return false;
    if (shift1.date !== shift2.date) return false;
    if (shift1.start !== shift2.start) return false;
    if (shift1.end !== shift2.end) return false;

    // Must have same slot configuration (roles and counts)
    return areSlotsEqual(shift1.slots, shift2.slots);
};

/**
 * Compares slot configurations
 */
const areSlotsEqual = (slots1, slots2) => {
    if (!slots1 || !slots2) return slots1 === slots2;
    if (slots1.length !== slots2.length) return false;

    // Sort slots by role for consistent comparison
    const sorted1 = [...slots1].sort((a, b) => (a.role || '').localeCompare(b.role || ''));
    const sorted2 = [...slots2].sort((a, b) => (a.role || '').localeCompare(b.role || ''));

    return sorted1.every((slot, index) =>
        slot.role === sorted2[index].role &&
        slot.count === sorted2[index].count
    );
};

/**
 * Checks if shifts fall within event date boundaries
 */
const checkDateBoundaries = (shifts, event) => {
    const issues = [];

    for (const shift of shifts) {
        if (shift.date < event.start) {
            issues.push({
                type: 'out_of_range',
                severity: 'error',
                shift,
                message: `Shift "${shift.name}" date (${shift.date}) is before event start (${event.start})`
            });
        }

        if (shift.date > event.end) {
            issues.push({
                type: 'out_of_range',
                severity: 'error',
                shift,
                message: `Shift "${shift.name}" date (${shift.date}) is after event end (${event.end})`
            });
        }
    }

    return issues;
};

/**
 * Safety check for duplicates within a series (shouldn't happen)
 */
const checkSeriesInternalDuplicates = (seriesShifts) => {
    const issues = [];
    const seen = new Set();

    for (const shift of seriesShifts) {
        const key = `${shift.name}-${shift.date}-${shift.start}-${shift.end}`;

        if (seen.has(key)) {
            issues.push({
                type: 'series_internal_duplicate',
                severity: 'error',
                shift,
                message: `Duplicate shift within series: "${shift.name}" on ${shift.date}`
            });
        }

        seen.add(key);
    }

    return issues;
};